import { Column } from '@ant-design/charts';
import { Button, Card, Col, Empty, List, Result, Row, Statistic } from 'antd';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Role, Smat, User } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';
import { SmatStatus } from '../smats/smat-status';
import moment from 'moment';
import { NavLink } from 'react-router-dom';

export function WelcomeWidget() {
  return (
    <Card title="Hoşgeldiniz!" size="small" bordered={false} style={{ height: '100%', overflow: 'hidden' }}>
      <p>İSG Uygulaması'na hoşgeldiniz.</p>
      <p>Uygulamayla ilgili bilgi almak için İSG Birimi'yle iletişime geçebilirsiniz.</p>
    </Card>
  );
}

export function SmatsWidget() {
  const [loading, setLoading] = useState(false);
  const [smats, setSmats] = useState<Smat[]>();

  const loadSmats = useCallback(() => {
    setLoading(true);

    const queryParams = [`year=2023`, `scope=user`, `isUserSummary=true`];
    const queryParamsStr = queryParams.join('&');

    apiClient
      .get(`smats?${queryParamsStr}`)
      .then(response => {
        setSmats(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadSmats();
  }, [loadSmats]);

  return (
    <>
      <Card title="SMAT'lar" size="small" bordered={false} style={{ overflow: 'hidden', height: '100%' }} loading={loading}>
        {!loading && !smats?.length && <Empty description="Tamamlanması gereken SMAT bulunmuyor." />}
        <div>
          {smats?.map(s => (
            <Row key={s.id} style={{ marginBottom: '0.5rem' }}>
              <Col span={6}>
                <NavLink to={`/smats/${s.id}`}>
                  {s.smatNo} ({s.week} / {s.area.code})
                </NavLink>
              </Col>
              <Col span={6}>{moment(s.startDate).format('DD.MM.yyyy')}</Col>
              <Col span={6}>{moment(s.endDate).format('DD.MM.yyyy')}</Col>
              <Col span={6}>
                <SmatStatus smat={s} />
              </Col>
            </Row>
          ))}
        </div>
      </Card>
    </>
  );
}

export function StatisticsWidget() {
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState<User[]>();
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>();
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = () => {
    setUsersLoading(true);
    apiClient
      .get('users')
      .then(response => {
        setUsers(response.data as User[]);
        setUsersLoading(false);
      })
      .catch(reason => {
        setUsersLoading(false);
        if (reason?.response?.status === 403) {
          setForbidden(true);
        }
      });
  };

  const loadRoles = () => {
    setRolesLoading(true);
    apiClient
      .get('roles')
      .then(response => {
        setRoles(response.data as Role[]);
        setRolesLoading(false);
      })
      .catch(reason => {
        setRolesLoading(false);
        if (reason?.response?.status === 403) {
          setForbidden(true);
        }
      });
  };

  const titleStyle: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <Card title="İstatistikler" size="small" bordered={false} style={{ overflow: 'hidden', height: '100%' }}>
      {!forbidden ? (
        <Row gutter={[12, 12]}>
          <Col sm={12} xxl={6}>
            <Card size="small">
              <Statistic
                title={<div style={titleStyle}>Kullanıcı</div>}
                value={users?.length}
                precision={0}
                valueStyle={{ color: '#3f8600' }}
                loading={usersLoading}
              />
            </Card>
          </Col>
          <Col sm={12} xxl={6}>
            <Card size="small">
              <Statistic
                title={<div style={titleStyle}>Rol</div>}
                value={roles?.length}
                precision={0}
                valueStyle={{ color: '#3f8600' }}
                loading={rolesLoading}
              />
            </Card>
          </Col>
        </Row>
      ) : (
        <Result status="warning" subTitle="İstatistikleri görüntülemek için yetkiniz bulunmuyor." />
      )}
    </Card>
  );
}

export function SavedSearchesWidget() {
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [savedSearches, setSavedSearches] = useState<string[]>();

  useEffect(() => {
    loadSavedExecutions();
  }, []);

  const loadSavedExecutions = () => {
    setLoading(true);
    apiClient
      .get('users/me/settings/searches')
      .then(response => {
        setSavedSearches(response.data as string[]);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const search = (value: string) => {
    if (!value || value.length < 3) {
      return;
    }
    history.push(`/search?s=${value}`);
  };

  return (
    <Card title="Kaydedilmiş aramalar" size="small" bordered={false} style={{ height: '100%', overflowY: 'auto' }}>
      <List
        loading={loading}
        dataSource={savedSearches}
        renderItem={(item: string) => (
          <List.Item>
            <Button type="link" onClick={() => search(item)}>
              {item}
            </Button>
          </List.Item>
        )}
      />
    </Card>
  );
}

export function Report1Widget() {
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState<any>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    // setLoading(true);
    // apiClient
    //   .get('grid-records/report1')
    //   .then(response => {
    //     const responseData = response.data;
    //     setAllData(responseData);
    //   })
    //   .catch((reason: any) => {
    //     apiErrorMessage(reason);
    //   })
    //   .finally(() => {
    //     setLoading(false);
    //   });
  };

  const config = {
    data: allData,
    xField: 'grid',
    yField: 'count',
    seriesField: 'status',
    isStack: true,
  };

  return (
    <Card
      title="Bölüm - risk bildirimi raporu"
      size="small"
      bordered={false}
      style={{ height: 'calc(100% - 1rem)', overflowY: 'scroll', overflowX: 'hidden' }}
    >
      <div style={{ height: '12rem', marginTop: '2rem' }}>
        <Column loading={loading} {...config} />
      </div>
    </Card>
  );
}
